import { forwardRef, memo } from 'react';
import styled, { css } from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useRenderColumns } from '../../columns';
import { GAP, MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../../constants';
import { useLayout } from '../layout-context';

export const LayoutGrid = memo(
  forwardRef<HTMLDivElement, unknown>((_, ref) => {
    const {
      breakpoints: { overflows, isMediumBreakpoint }
    } = useLayout();
    const columns = useRenderColumns();

    return (
      <Container
        ref={ref}
        $columnsCount={columns.length}
        $isMediumBreakpoint={isMediumBreakpoint}
        $overflows={overflows}
      >
        {columns}
      </Container>
    );
  })
);

export const Container = styled.div.attrs<{
  $columnsCount: number;
  $isMediumBreakpoint: boolean;
  $overflows: boolean;
}>({
  className: createReadableClass(
    {
      component: 'layout-grid',
      element: 'container'
    },
    ['es-layout-component']
  )
})`
  display: grid;
  justify-content: center;
  align-content: center;
  row-gap: ${GAP * 2}px;
  grid-template-columns: ${({
    $columnsCount,
    $isMediumBreakpoint,
    $overflows
  }) => {
    const column = `minmax(${MIN_COLUMN_WIDTH}px, ${MAX_COLUMN_WIDTH}px)`;
    if (!$overflows) {
      return `repeat(${$columnsCount}, ${column})`;
    }

    if ($isMediumBreakpoint) {
      const columns = `repeat(2, minmax(${MIN_COLUMN_WIDTH / 2}px, ${
        MAX_COLUMN_WIDTH / 2
      }px))`;

      return `${columns} ${GAP}px ${columns}`;
    }

    return column;
  }};

  ${({ $isMediumBreakpoint }) =>
    !$isMediumBreakpoint && `column-gap: ${GAP}px`};

  ${({ $columnsCount, $isMediumBreakpoint }) =>
    $isMediumBreakpoint &&
    css`
      & > div:nth-child(odd) {
        grid-column: span 2;
      }

      & > div:nth-child(even) {
        grid-column: 4 / span 2;
      }

      ${$columnsCount % 2 &&
      `
        & > div:last-child {
          grid-column: 2 / span 3;
          margin: 0 ${GAP / 2}px;
        }
      `}
    `}
`;
